import { useEffect, useState } from "react";

export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handle);
    window.addEventListener("orientationchange", handle);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("orientationchange", handle);
    };
  }, [breakpoint]);
  return isMobile;
}

/**
 * Live pixel size proportional to the viewport's smaller dimension (vmin),
 * clamped to [min, max]. For sizing things driven by numeric props (e.g.
 * canvas/SVG size attributes) that can't be expressed as a CSS clamp() string.
 */
export function useVminSize(
  fraction: number,
  { min, max }: { min: number; max: number },
) {
  const compute = () => {
    if (typeof window === "undefined") return min;
    const vmin = Math.min(window.innerWidth, window.innerHeight);
    return Math.round(Math.min(max, Math.max(min, vmin * fraction)));
  };
  const [size, setSize] = useState(compute);
  useEffect(() => {
    const handle = () => setSize(compute());
    window.addEventListener("resize", handle);
    window.addEventListener("orientationchange", handle);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("orientationchange", handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fraction, min, max]);
  return size;
}

/**
 * Index of the current stage while `active` is true: starts at 0 and moves to stage i + 1 once
 * `stageStartsMs[i]` milliseconds have passed. Resets to 0 when `active` turns false.
 * For showing progress text on requests that don't report their own progress.
 */
export function useTimedStage(active: boolean, stageStartsMs: number[]) {
  const [stage, setStage] = useState(0);
  const stagesKey = stageStartsMs.join(",");
  useEffect(() => {
    setStage(0);
    if (!active) return;
    const timers = stageStartsMs.map((ms, index) =>
      setTimeout(() => setStage(index + 1), ms),
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stagesKey]);
  return stage;
}
